import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  App,
  AutoComplete,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Spin,
} from 'antd';
import { styled } from 'styled-components';
import { useLocations } from '@/api/Locations';
import {
  useSavePortCall,
  useSaveVesselJourney,
  useVesselAgents,
  useVesselBerths,
  useVesselJourneys,
  useVesselPorts,
  useVessels,
} from '@/api/Vessel';
import { useTranslate } from '@/translations';
import { VesselJourney } from '@/types';

type AddJourneyModalProps = {
  open: boolean;
  onClose: () => void;
};

type PortCallFormValues = {
  vesselId: number;
  voyage: string;
  originPortId?: number;
  siteId: number;
  destinationCompoundId?: number;
  eta?: dayjs.Dayjs;
  agentId?: number;
  plannedBerthId?: number;
  notes?: string;
};

const pendingMenu = (pending?: boolean) =>
  pending ? <Spin size="small" /> : undefined;

const sameText = (left?: string, right?: string) =>
  (left ?? '').trim().toLowerCase() === (right ?? '').trim().toLowerCase();

const journeyValue = (journey: VesselJourney) =>
  journey.voyage?.trim() || `#${journey.id}`;

const journeyOptionLabel = (journey: VesselJourney) => {
  const route = [journey.originPortName, journey.destinationPortName]
    .filter(Boolean)
    .join(' → ');
  const voyage = journeyValue(journey);
  return route ? `${voyage} · ${route}` : voyage;
};

const matchJourney = (
  journeys: VesselJourney[],
  voyage?: string,
  selectedJourneyId?: number,
) => {
  const selected = journeys.find((journey) => journey.id === selectedJourneyId);
  if (selected && sameText(journeyValue(selected), voyage)) return selected;
  const typed = voyage?.trim();
  if (!typed) return undefined;
  return journeys.find((journey) => sameText(journey.voyage, typed));
};

const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
`;

export const AddJourneyModal = ({ open, onClose }: AddJourneyModalProps) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [form] = Form.useForm<PortCallFormValues>();
  const vesselId = Form.useWatch('vesselId', form);
  const voyage = Form.useWatch('voyage', form);
  const savePortCall = useSavePortCall();
  const saveJourney = useSaveVesselJourney();
  const vessels = useVessels(open);
  const ports = useVesselPorts(open);
  const agents = useVesselAgents(open);
  const berths = useVesselBerths(open);
  const locations = useLocations({ enabled: open });
  const journeys = useVesselJourneys(vesselId, open && Boolean(vesselId));
  const locationsLoading = locations.isPending || locations.isFetching;
  const journeysLoading =
    Boolean(vesselId) && (journeys.isPending || journeys.isFetching);
  const [selectedJourneyId, setSelectedJourneyId] = useState<number>();
  const selectedJourneyIdRef = useRef<number | undefined>(undefined);
  const previousVesselId = useRef<number | undefined>(vesselId);
  const journeyRows = journeys.data ?? [];
  const matched = matchJourney(journeyRows, voyage, selectedJourneyId);
  const journeyOptions = journeyRows.map((journey) => ({
    key: String(journey.id),
    value: journey.voyage?.trim() || `#${journey.id}`,
    label: journeyOptionLabel(journey),
    journeyId: journey.id,
  }));

  useEffect(() => {
    if (previousVesselId.current === vesselId) return;
    previousVesselId.current = vesselId;
    selectedJourneyIdRef.current = undefined;
    setSelectedJourneyId(undefined);
    form.setFieldsValue({ voyage: undefined, originPortId: undefined });
  }, [form, vesselId]);

  useEffect(() => {
    if (!matched?.originPortId) return;
    form.setFieldValue('originPortId', matched.originPortId);
  }, [form, matched?.id, matched?.originPortId]);
  const vesselOptions = (vessels.data ?? [])
    .filter((vessel) => vessel.isActive)
    .map((vessel) => ({
      value: vessel.id,
      label: vessel.imo
        ? `${vessel.vesselName} (${vessel.imo})`
        : vessel.vesselName,
    }));
  const portOptions = (ports.data ?? [])
    .filter((port) => port.isActive)
    .map((port) => ({
      value: port.id,
      label: port.locode ? `${port.portName} (${port.locode})` : port.portName,
    }));
  const agentOptions = (agents.data ?? [])
    .filter((agent) => agent.isActive)
    .map((agent) => ({ value: agent.id, label: agent.name }));
  const berthOptions = (berths.data ?? [])
    .filter((berth) => berth.isActive)
    .map((berth) => ({ value: berth.id, label: berth.name }));
  const locationOptions = (locations.data ?? []).map((location) => ({
    value: location.id,
    label: location.desc || location.code,
  }));

  const close = () => {
    selectedJourneyIdRef.current = undefined;
    setSelectedJourneyId(undefined);
    previousVesselId.current = undefined;
    form.resetFields();
    onClose();
  };

  const rememberJourney = (journeyId?: number) => {
    selectedJourneyIdRef.current = journeyId;
    setSelectedJourneyId(journeyId);
  };

  const create = async (values: PortCallFormValues) => {
    const typedVoyage = values.voyage.trim();
    const existing = matchJourney(
      journeyRows,
      typedVoyage,
      selectedJourneyIdRef.current,
    );
    const voyageRef = (existing?.voyage?.trim() || typedVoyage).trim();
    let vesselJourneyId = existing?.id;
    if (!vesselJourneyId) {
      const created = await saveJourney.mutateAsync({
        vesselId: values.vesselId,
        originPortId: values.originPortId,
        voyage: voyageRef,
        plannedArrival: values.eta ? values.eta.toISOString() : undefined,
      });
      if (!created.success || !created.data) {
        message.error(created.message || t('generalError'));
        return;
      }
      vesselJourneyId = created.data.id;
    }
    const result = await savePortCall.mutateAsync({
      vesselId: values.vesselId,
      vesselJourneyId,
      voyage: voyageRef,
      originPortId: existing ? existing.originPortId : values.originPortId,
      siteId: values.siteId,
      destinationCompoundId: values.destinationCompoundId,
      eta: values.eta ? values.eta.toISOString() : undefined,
      agentId: values.agentId,
      plannedBerthId: values.plannedBerthId,
      notes: values.notes,
    });
    if (!result.success || !result.data) {
      message.error(result.message || t('generalError'));
      return;
    }
    message.success(t('addJourneyPortCall'));
    close();
  };

  return (
    <Modal
      title={t('addJourneyPortCall')}
      open={open}
      width={760}
      onCancel={close}
      destroyOnHidden
      okText={t('createPortCall')}
      confirmLoading={savePortCall.isPending || saveJourney.isPending}
      onOk={() => form.submit()}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => void create(values)}
      >
        <Fields>
          <Form.Item
            name="vesselId"
            label={t('vessel')}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={vesselOptions}
              loading={vessels.isPending || vessels.isFetching}
              notFoundContent={pendingMenu(
                vessels.isPending || vessels.isFetching,
              )}
            />
          </Form.Item>
          <Form.Item
            name="eta"
            label={t('etaUtc')}
            rules={[{ required: true }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
          <Form.Item
            name="voyage"
            label={t('voyageJourneyRef')}
            rules={[{ required: true, whitespace: true }]}
          >
            <AutoComplete
              allowClear
              options={journeyOptions}
              placeholder="V026"
              disabled={!vesselId}
              filterOption={(input, option) => {
                const query = input.trim().toLowerCase();
                if (!query) return true;
                return [option?.label, option?.value].some((item) =>
                  String(item ?? '')
                    .toLowerCase()
                    .includes(query),
                );
              }}
              notFoundContent={pendingMenu(journeysLoading)}
              onSelect={(_value, option) => {
                const journeyId = option.journeyId as number | undefined;
                rememberJourney(journeyId);
                const journey = journeyRows.find(
                  (item) => item.id === journeyId,
                );
                if (journey?.originPortId) {
                  form.setFieldValue('originPortId', journey.originPortId);
                }
              }}
              onChange={(value) => {
                const next = String(value ?? '');
                const selected = journeyRows.find(
                  (item) => item.id === selectedJourneyIdRef.current,
                );
                if (!selected || !sameText(journeyValue(selected), next)) {
                  rememberJourney(undefined);
                }
              }}
            />
          </Form.Item>
          <Form.Item name="originPortId" label={t('originPort')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={portOptions}
              disabled={Boolean(matched) || journeysLoading}
              loading={ports.isPending || ports.isFetching || journeysLoading}
              notFoundContent={pendingMenu(
                ports.isPending || ports.isFetching || journeysLoading,
              )}
            />
          </Form.Item>
          <Form.Item
            name="siteId"
            label={t('siteTerminal')}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={locationOptions}
              loading={locationsLoading}
              notFoundContent={pendingMenu(locationsLoading)}
            />
          </Form.Item>
          <Form.Item
            name="destinationCompoundId"
            label={t('destinationCompound')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={locationOptions}
              loading={locationsLoading}
              notFoundContent={pendingMenu(locationsLoading)}
            />
          </Form.Item>
          <Form.Item name="agentId" label={t('agent')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('chooseAgent')}
              options={agentOptions}
              loading={agents.isPending || agents.isFetching}
              notFoundContent={pendingMenu(
                agents.isPending || agents.isFetching,
              )}
            />
          </Form.Item>
          <Form.Item name="plannedBerthId" label={t('berth')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('chooseBerth')}
              options={berthOptions}
              loading={berths.isPending || berths.isFetching}
              notFoundContent={pendingMenu(
                berths.isPending || berths.isFetching,
              )}
            />
          </Form.Item>
        </Fields>
        <Form.Item name="notes" label={t('notes')}>
          <Input.TextArea rows={3} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
